// src/components/AdminCheckoutModal.jsx
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { stripePromise } from "../../lib/stripe"; // NEW
import { createCheckoutSession } from "../../api/payments"; // NEW

export default function AdminCheckoutModal({
  open,
  onClose,
  carId,
  basePrice = 0,
  includeWarranty,
  warrantyPackageName,
  warrantyTerm,
  warrantyOption = { price: 0, perRepairLimit: 0 },
  totalPrice = 0,
  carName = "Car Purchase", // NEW (optional)
}) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
    street: "",
    city: "",
    postcode: "",
    country: "",
  });
  const [loading, setLoading] = useState(false);

  function handleChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const stripe = await stripePromise;

      // Build a readable warranty label for the Stripe line item
      const warrantyLabel =
        includeWarranty && warrantyPackageName
          ? `${warrantyPackageName}${
              warrantyTerm ? ` · ${warrantyTerm} mo` : ""
            }${warrantyOption?.price ? ` (£${warrantyOption.price})` : ""}`
          : "";

      // Create Checkout Session (backend will use metadata + webhook to mint the order)
      const { id: sessionId } = await createCheckoutSession({
        carId,
        carName,
        totalPrice, // already includes warranty if selected
        includeWarranty,
        warrantyLabel,
        // pass key customer fields (Stripe will still collect/confirm at checkout)
        customer: {
          name: `${form.firstName} ${form.lastName}`.trim(),
          email: form.email,
          phone: form.contactNumber,
        },
        buyer: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          contactNumber: form.contactNumber,
          street: form.street,
          city: form.city,
          postcode: form.postcode,
          country: form.country,
        },
        // you can also pass address to your own DB later if needed
        successRedirectPath: "/checkout/success",
        cancelRedirectPath: `/cars/${carId}`,
      });

      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) alert(error.message || "Stripe redirect failed.");
    } catch (err) {
      console.error(err);
      alert(
        err.response?.data?.error || err.message || "Failed to start checkout."
      );
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-[#1f2954]/90 p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      {/* Container */}
      <div className="relative w-full max-w-4xl bg-white text-primary rounded-xl shadow-2xl">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-8 sm:top-4 right-4 text-secondary hover:text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <span className="sr-only">Close</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="p-8">
          <h2 className="text-2xl font-bold text-accent mb-6">
            Admin Checkout
          </h2>

          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-8"
          >
            {/* Customer fields */}
            {["firstName", "lastName", "email", "contactNumber"].map(
              (field) => {
                const label = field
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase());
                return (
                  <div key={field}>
                    <label
                      htmlFor={field}
                      className="block text-sm font-medium text-secondary-accent"
                    >
                      {label}
                    </label>
                    <input
                      id={field}
                      name={field}
                      type={field === "email" ? "email" : "text"}
                      value={form[field]}
                      onChange={handleChange}
                      required
                      className="mt-1 block w-full rounded-lg bg-white px-3 py-2 text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                );
              }
            )}

            {/* —— Address section —— */}
            <div className="sm:col-span-2 border-t pt-4">
              <h3 className="text-lg font-medium text-secondary-accent mb-2">
                Address
              </h3>
            </div>
            {[
              ["street", "Street address"],
              ["city", "City"],
              ["postcode", "Postcode / ZIP"],
              ["country", "Country"],
            ].map(([field, label]) => (
              <div key={field}>
                <label
                  htmlFor={field}
                  className="block text-sm font-medium text-secondary-accent"
                >
                  {label}
                </label>
                <input
                  id={field}
                  name={field}
                  type="text"
                  value={form[field]}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-lg bg-white px-3 py-2 text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            ))}

            {/* Order summary */}
            <div className="lg:col-span-2 border-t pt-4 space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Vehicle Price:</span>
                <span>
                  £{basePrice != null ? basePrice.toLocaleString() : "0"}
                </span>
              </div>
              {includeWarranty && (
                <div className="flex justify-between">
                  <span>Warranty:</span>
                  <span>
                    £
                    {warrantyOption?.price != null
                      ? warrantyOption.price.toLocaleString()
                      : "0"}
                  </span>
                </div>
              )}
              <div className="flex justify-between font-bold">
                <span>Total:</span>
                <span>
                  £{totalPrice != null ? totalPrice.toLocaleString() : "0"}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="lg:col-span-2 flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 text-sm font-medium text-secondary hover:text-on-surface focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-accent"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              >
                {loading ? "Redirecting…" : "Pay with Stripe"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}

// // src/components/AdminCheckoutModal.jsx
// import React, { useState } from "react";
// import { createPortal } from "react-dom";
// import { createAdminOrder } from "../../api/orders";

// export default function AdminCheckoutModal({
//   open,
//   onClose,
//   carId,
//   basePrice = 0,
//   includeWarranty,
//   warrantyPackageName,
//   warrantyTerm,
//   warrantyOption = { price: 0, perRepairLimit: 0 },
//   totalPrice = 0,
// }) {
//   const [form, setForm] = useState({
//     firstName: "",
//     lastName: "",
//     email: "",
//     contactNumber: "",
//     street: "",
//     city: "",
//     postcode: "",
//     country: "",
//   });
//   const [loading, setLoading] = useState(false);
//   const [success, setSuccess] = useState(false);
//   const [orderNumber, setOrderNumber] = useState(null);

//   function handleChange(e) {
//     setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
//   }

//   async function handleSubmit(e) {
//     e.preventDefault();
//     setLoading(true);
//     try {
//       const payload = {
//         vehicle: carId,
//         ...form,
//         address: {
//           street: form.street,
//           city: form.city,
//           postcode: form.postcode,
//           country: form.country,
//         },
//         includeWarranty,
//         warrantyPackageName: includeWarranty ? warrantyPackageName : null,
//         warrantyTerm: includeWarranty ? warrantyTerm : null,
//         warrantyPrice: includeWarranty ? warrantyOption.price : 0,
//         perRepairLimit: includeWarranty ? warrantyOption.perRepairLimit : 0,
//         amount: totalPrice,
//       };
//       const response = await createAdminOrder(payload);
//       // console.log("order details",response)
//       setOrderNumber(response.order.orderNumber);
//       setSuccess(true);
//     } catch (err) {
//       console.error(err);
//       alert("Failed to place order.");
//     } finally {
//       setLoading(false);
//     }
//   }

//   if (!open) return null;
//   return createPortal(
//     <div
//       className="fixed inset-0 flex items-center justify-center bg-[#1f2954]/90 p-4 z-50"
//       role="dialog"
//       aria-modal="true"
//     >
//       {/* Container */}
//       <div className="relative w-full max-w-4xl bg-white text-primary rounded-xl shadow-2xl">
//         {/* Close button */}
//         <button
//           type="button"
//           onClick={onClose}
//           className="
//             absolute top-8 sm:top-4 right-4
//             text-secondary hover:text-on-surface
//             focus:outline-none focus:ring-2 focus:ring-primary
//           "
//         >
//           <span className="sr-only">Close</span>
//           <svg
//             xmlns="http://www.w3.org/2000/svg"
//             className="h-6 w-6"
//             fill="none"
//             viewBox="0 0 24 24"
//             stroke="currentColor"
//           >
//             <path
//               strokeLinecap="round"
//               strokeLinejoin="round"
//               strokeWidth={2}
//               d="M6 18L18 6M6 6l12 12"
//             />
//           </svg>
//         </button>

//         <div className="p-8">
//           {success ? (
//             <div className="text-center space-y-6">
//               <h2 className="text-2xl font-semibold text-on-surface mb-2">
//                 Order Placed!
//               </h2>
//               <p className="text-lg text-secondary-accent">
//                 Your order has been created. <br />
//                 <strong>Order ID:</strong> {orderNumber}
//               </p>
//               <button
//                 onClick={() => {
//                   setSuccess(false);
//                   onClose();
//                 }}
//                 className="
//                   mt-4 px-6 py-2 bg-accent text-white rounded hover:bg-accent/90 transition
//                   focus:outline-none focus:ring-2 focus:ring-primary
//                 "
//               >
//                 Close
//               </button>
//             </div>
//           ) : (
//             <>
//               <h2 className="text-2xl font-bold text-accent mb-6">
//                 Admin Checkout
//               </h2>

//               <form
//                 onSubmit={handleSubmit}
//                 className="
//                   grid
//                   grid-cols-1        /* 1-col on xs */
//                   sm:grid-cols-2     /* 2-cols on ≥640px */
//                   gap-y-6
//                   gap-x-8
//                 "
//               >
//                 {/* Customer fields */}
//                 {["firstName", "lastName", "email", "contactNumber"].map(
//                   (field) => {
//                     const label = field
//                       .replace(/([A-Z])/g, " $1")
//                       .replace(/^./, (str) => str.toUpperCase());
//                     return (
//                       <div key={field}>
//                         <label
//                           htmlFor={field}
//                           className="block text-sm font-medium text-secondary-accent"
//                         >
//                           {label}
//                         </label>
//                         <input
//                           id={field}
//                           name={field}
//                           type={field === "email" ? "email" : "text"}
//                           value={form[field]}
//                           onChange={handleChange}
//                           required
//                           className="
//             mt-1 block w-full rounded-lg
//             bg-white px-3 py-2 text-primary
//             focus:outline-none focus:ring-2 focus:ring-primary
//           "
//                         />
//                       </div>
//                     );
//                   }
//                 )}

//                 {/* —— Address section —— */}
//                 <div className="sm:col-span-2 border-t pt-4">
//                   <h3 className="text-lg font-medium text-secondary-accent mb-2">
//                     Address
//                   </h3>
//                 </div>
//                 {[
//                   ["street", "Street address"],
//                   ["city", "City"],
//                   ["postcode", "Postcode / ZIP"],
//                   ["country", "Country"],
//                 ].map(([field, label]) => (
//                   <div key={field}>
//                     <label
//                       htmlFor={field}
//                       className="block text-sm font-medium text-secondary-accent"
//                     >
//                       {label}
//                     </label>
//                     <input
//                       id={field}
//                       name={field}
//                       type="text"
//                       value={form[field]}
//                       onChange={handleChange}
//                       required
//                       className="mt-1 block w-full rounded-lg bg-white px-3 py-2 text-primary focus:outline-none focus:ring-2 focus:ring-primary"
//                     />
//                   </div>
//                 ))}

//                 {/* Order summary */}
//                 <div className="lg:col-span-2 border-t pt-4 space-y-1 text-sm">
//                   <div className="flex justify-between">
//                     <span>Vehicle Price:</span>
//                     <span>
//                       £{basePrice != null ? basePrice.toLocaleString() : "0"}
//                     </span>
//                   </div>
//                   {includeWarranty && (
//                     <div className="flex justify-between">
//                       <span>Warranty:</span>
//                       <span>
//                         £
//                         {warrantyOption?.price != null
//                           ? warrantyOption.price.toLocaleString()
//                           : "0"}
//                       </span>
//                     </div>
//                   )}
//                   <div className="flex justify-between font-bold">
//                     <span>Total:</span>
//                     <span>
//                       £{totalPrice != null ? totalPrice.toLocaleString() : "0"}
//                     </span>
//                   </div>
//                 </div>

//                 {/* Actions */}
//                 <div className="lg:col-span-2 flex justify-end space-x-4">
//                   <button
//                     type="button"
//                     onClick={onClose}
//                     className="
//                       px-5 py-2 text-sm font-medium
//                       text-secondary hover:text-on-surface
//                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-accent
//                     "
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     type="submit"
//                     disabled={loading}
//                     className="
//                       px-5 py-2 text-sm font-medium
//                       bg-accent text-white rounded-lg
//                       hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary
//                       disabled:opacity-50
//                     "
//                   >
//                     {loading ? "Placing…" : "Place Order"}
//                   </button>
//                 </div>
//               </form>
//             </>
//           )}
//         </div>
//       </div>
//     </div>,
//     document.body
//   );
// }
